import { TextField, TextFieldProps, Box, Typography } from '@mui/material';
import { forwardRef, useMemo } from 'react';

/**
 * Reusable form textarea component that wraps Material-UI TextField with multiline
 * support, consistent styling, and character counter.
 *
 * Features:
 * - Multiline textarea support with configurable rows
 * - Character counter display
 * - Consistent sizing and spacing
 * - Mobile-optimized (prevents iOS auto-zoom with 16px font)
 * - Standardized error and focus states
 * - Accessibility-ready with ARIA attributes
 * - Separate label and input styling
 */
export interface FormTextAreaFieldProps extends Omit<TextFieldProps, 'variant' | 'multiline'> {
  /**
   * Maximum character length for input
   * @default undefined (no limit)
   */
  maxLength?: number;

  /**
   * Minimum number of rows for the textarea
   * @default 4
   */
  minRows?: number;

  /**
   * Whether to show the character counter
   * @default true when maxLength is set
   */
  showCounter?: boolean;
}

export const FormTextAreaField = forwardRef<HTMLInputElement, FormTextAreaFieldProps>(
  ({ maxLength, minRows = 4, showCounter, label, required, sx, value, ...props }, ref) => {
    const shouldShowCounter = showCounter ?? maxLength !== undefined;
    const currentLength = typeof value === 'string' ? value.length : 0;

    const counterLabel = useMemo(() => {
      if (!shouldShowCounter || !maxLength) return '';
      return `${currentLength}/${maxLength}`;
    }, [shouldShowCounter, currentLength, maxLength]);

    return (
      <Box sx={{ width: '100%' }}>
        {label && (
          <Typography
            component="label"
            htmlFor={props.id}
            sx={{
              display: 'block',
              fontSize: '14px',
              fontWeight: 500,
              color: props.error ? 'error.main' : 'text.primary',
              mb: 1,
            }}
          >
            {label}
            {required && (
              <Typography component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                *
              </Typography>
            )}
          </Typography>
        )}
        <TextField
          ref={ref}
          variant="outlined"
          fullWidth
          multiline
          minRows={minRows}
          value={value}
          InputProps={{
            ...props.InputProps,
          }}
          inputProps={{
            maxLength,
            'aria-required': required || undefined,
            'data-testid': props.id ? `form-textarea-${props.id}` : undefined,
            ...props.inputProps,
          }}
          sx={{
            '& .MuiInputBase-root': {
              borderRadius: '10px',
              backgroundColor: 'background.paper',
              alignItems: 'flex-start',
            },
            '& .MuiInputBase-input': {
              fontSize: { xs: '16px', sm: '14px' }, // Prevent iOS auto-zoom on mobile
              padding: '12px',
              lineHeight: 1.6,
            },
            '& .MuiFormHelperText-root': {
              marginLeft: 0,
              marginTop: '6px',
              fontSize: '0.875rem',
            },
            // Default border - #E5E7EB
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: '#E5E7EB',
              borderWidth: '1px',
            },
            // Hover state
            '& .MuiOutlinedInput-root:hover:not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline':
              {
                borderColor: 'primary.main',
              },
            // Focus state - primary color border
            '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderWidth: 2,
              borderColor: 'primary.main',
            },
            // Error state - ensure visibility
            '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
              borderWidth: 2,
            },
            ...sx,
          }}
          {...props}
          label={undefined}
        />
        {shouldShowCounter && maxLength && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'right',
              mt: 0.5,
              color: currentLength > maxLength ? 'error.main' : 'text.secondary',
            }}
            data-testid={props.id ? `${props.id}-counter` : 'textarea-counter'}
          >
            {counterLabel}
          </Typography>
        )}
      </Box>
    );
  }
);

FormTextAreaField.displayName = 'FormTextAreaField';
